import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@fluentui/react-components';
import { Building2, Laptop, Link2, Plus, Rocket, Users } from 'lucide-react';

import { getMigrationGroups } from '../api/migrationGroups';
import { getMigrations } from '../api/migrations';
import { getTenants } from '../api/tenantRegistry';
import { migrationTypeLabel } from '../lib/vocabulary';
import Donut from '../components/Donut';
import {
  AbLink,
  HeroBand,
  MetricCard,
  MetricRow,
  SectionPanel,
  StatePill,
} from '../components/abPrimitives';
import { deriveCampaignStatus } from '../lib/campaignStatus';
import { campaignsByWaveState } from '../lib/homeMetrics';
import { STROKE, NUMERIC, STATUS, SURFACE, TEXT } from '../theme/tokens';
import { QueryGate } from '../components/routePrimitives';

// The AirBridge command centre (FD-458).
//
// EVERY NUMBER ON THIS PAGE COMES FROM TWO CALLS THE CONSOLE ALREADY MAKES —
// `GET /v1/migration-groups` (whose rollup is computed server-side from rows it
// had already read) and a bounded page of `GET /v1/migrations`. A landing page
// that fanned out per campaign would make the first screen the most expensive
// one in the console.
//
// FOUR THINGS THE MOCKUP SHOWS THAT ARE NOT HERE, each because the platform
// cannot produce them honestly (FD-ADR-043 D4):
//
//   * "↑ 12%" deltas — nothing stores a previous value.
//   * "Devices by migration stage" across the deployment — that needs a device
//     partition read per campaign. `GET /v1/overview` is filed as an optional
//     follow-on; until it exists the donut charts CAMPAIGNS BY WAVE STATE and
//     its title says so.
//   * "Recent activity" — FlightDeckEvents is partitioned by jobId, so there is
//     no cross-campaign feed to read. Not approximated.
//   * "Total identities" — a cross-partition scan with no total. The tile would
//     be a lie or a scan on every visit; the Identities page owns that question.

/**
 * The first screen on a deployment that has never been used (FD-482).
 *
 * What it replaces is the argument for it: four zeroes, an empty table, an
 * empty donut and two buttons. None of that is information — a chart of nothing
 * reads as a BROKEN chart rather than an empty one, and an operator who has
 * just signed in to a fresh environment learns nothing about what to do first.
 *
 * The three steps are listed because a campaign is not self-explanatory. An
 * operator who creates one and stops has a row and no migration; the wave
 * activation is the thing that actually mints the credential and lets devices
 * fly, and it is worth saying so before they start rather than after.
 *
 * Every step named here is a real operation with a real endpoint. Nothing is
 * aspirational (FD-ADR-043 D4).
 */
function FirstRun({
  hasTenants,
  onConnectTenant,
  onCreateCampaign,
}: {
  hasTenants: boolean;
  onConnectTenant: () => void;
  onCreateCampaign: () => void;
}) {
  // R-FD-055 — connecting the tenants comes FIRST. A campaign names a destination
  // (and, for Cloud, a source) tenant, and nothing can run in a tenant until it is
  // connected, has its own application, and that application has been granted.
  // Until R-FD-055 there was no way to do that before the campaign existed.
  const steps = [
    {
      title: 'Connect your tenants',
      detail: 'Sign in to each source and destination tenant once, so this environment can work in them.',
      done: hasTenants,
    },
    {
      title: 'Create a campaign',
      detail: 'A set of devices moving to one destination. Hybrid joins a domain to Entra; Cloud moves between tenants.',
      done: false,
    },
    {
      title: 'Assign devices',
      detail: 'Add serial numbers to the campaign. Each one is registered so it can find this deployment at boot.',
      done: false,
    },
    {
      title: 'Activate the wave',
      detail: 'A guided sign-in mints the join credential and builds the provisioning package. Devices migrate from there.',
      done: false,
    },
  ];

  return (
    <div
      className="flex flex-col items-center gap-6 rounded-lg border px-6 py-14 text-center"
      style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
    >
      <Rocket size={30} strokeWidth={1.5} style={{ color: TEXT.disabled }} />

      <div className="flex max-w-xl flex-col gap-2">
        <h2 className="m-0 text-[20px] font-semibold" style={{ color: TEXT.primary }}>
          Let&rsquo;s set up your first migration campaign
        </h2>
        <p className="m-0 text-[13px] leading-relaxed" style={{ color: TEXT.secondary }}>
          Nothing has been migrated in this environment yet. Start by connecting the tenants you
          migrate from and to; a campaign is then where devices, identities and a destination
          tenant come together, and everything else on this page fills in once one exists.
        </p>
      </div>

      {/* The primary action is the next step that is not done: connect first, then
          create. Both stay on screen, because an operator who already knows their
          tenants are connected elsewhere must not be forced through a page they do
          not need. */}
      <div className="flex flex-wrap justify-center gap-2">
        <Button
          appearance={hasTenants ? 'secondary' : 'primary'}
          size="large"
          icon={<Link2 size={16} strokeWidth={1.5} />}
          onClick={onConnectTenant}
        >
          Connect a tenant
        </Button>
        <Button
          appearance={hasTenants ? 'primary' : 'secondary'}
          size="large"
          icon={<Plus size={16} strokeWidth={1.5} />}
          onClick={onCreateCampaign}
        >
          New campaign
        </Button>
      </div>

      <ol className="m-0 grid w-full max-w-4xl list-none grid-cols-1 gap-3 p-0 text-left sm:grid-cols-2 lg:grid-cols-4">
        {steps.map((step, index) => (
          <li
            key={step.title}
            className="rounded-md border px-4 py-3"
            style={{ borderColor: STROKE.subtle, backgroundColor: SURFACE.rowFill }}
          >
            <div
              className="mb-1 text-[10px] font-semibold uppercase"
              style={{ color: TEXT.secondary, letterSpacing: '0.1em', ...NUMERIC }}
            >
              Step {index + 1}
              {step.done && (
                <span className="ml-2 normal-case" style={{ color: STATUS.successText, letterSpacing: 'normal' }}>
                  · Done
                </span>
              )}
            </div>
            <div className="text-[13px] font-semibold" style={{ color: TEXT.primary }}>
              {step.title}
            </div>
            <p className="m-0 mt-1 text-[12px] leading-relaxed" style={{ color: TEXT.secondary }}>
              {step.detail}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();

  const groups = useQuery({ queryKey: ['migration-groups'], queryFn: getMigrationGroups });
  const tenants = useQuery({ queryKey: ['tenants', {}], queryFn: () => getTenants() });
  const migrations = useQuery({
    queryKey: ['migrations', { pageSize: 50 }],
    queryFn: () => getMigrations({ pageSize: 50 }),
  });

  const totals = groups.data?.totals;

  // FIRST RUN — no campaigns AND no groups.
  //
  // The condition is the one `GroupsListPage` already uses, and the "and" is the
  // decision: "no groups" ALONE is ordinary (FD-ADR-030 — groups are optional
  // and most campaigns will never be in one), so a panel that fired on it would
  // push operators into busywork. Both empty means the deployment has never been
  // used.
  //
  // It costs no request. Home already fetches both, and an empty first page of
  // `GET /v1/migrations` is an empty table — the endpoint returns a page, so
  // zero rows in it means zero rows.
  const isFirstRun =
    groups.isSuccess &&
    migrations.isSuccess &&
    (groups.data?.groups.length ?? 0) === 0 &&
    (migrations.data?.migrations.length ?? 0) === 0;
  const recent = (migrations.data?.migrations ?? [])
    .slice()
    .sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''))
    .slice(0, 6);

  return (
    <>
      <HeroBand
        eyebrow="Welcome to AirBridge"
        title="Move forward with confidence."
        subtitle="Plan, migrate and validate Windows devices across domains and tenants — in one place."
      />

      <QueryGate isLoading={groups.isLoading} error={groups.error} label="the workspace">
        {isFirstRun ? (
          <FirstRun
            hasTenants={(tenants.data?.length ?? 0) > 0}
            onConnectTenant={() => navigate('/tenants/connect')}
            onCreateCampaign={() => navigate('/migrations/new')}
          />
        ) : (
        <>
        <MetricRow>
          <MetricCard
            label="Devices assigned"
            value={(totals?.deviceCount ?? 0).toLocaleString()}
            caption="Across every campaign"
            icon={<Laptop size={18} strokeWidth={1.5} />}
          />
          <MetricCard
            label="Campaigns"
            value={(totals?.migrationCount ?? 0).toLocaleString()}
            caption={`${groups.data?.groups.length ?? 0} groups`}
            icon={<Rocket size={18} strokeWidth={1.5} />}
          />
          <MetricCard
            label="Active waves"
            value={(totals?.byBprtStatus?.active ?? 0).toLocaleString()}
            caption="Credential minted, devices flying"
            icon={<Users size={18} strokeWidth={1.5} />}
            tone="progress"
          />
          <MetricCard
            label="Tenants"
            value={(tenants.data?.length ?? 0).toLocaleString()}
            caption="Source and destination"
            icon={<Building2 size={18} strokeWidth={1.5} />}
          />
        </MetricRow>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <SectionPanel
            title="Recent campaigns"
            description="The six most recently updated."
            action={<AbLink to="/migrations">View all campaigns →</AbLink>}
            bodyClassName="pb-2"
          >
            <QueryGate
              isLoading={migrations.isLoading}
              error={migrations.error}
              label="campaigns"
            >
              {recent.length === 0 ? (
                <p className="px-5 pb-4 text-[13px]" style={{ color: TEXT.secondary }}>
                  No campaigns yet.
                </p>
              ) : (
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr>
                      {['Campaign', 'Type', 'Devices', 'State'].map((heading, index) => (
                        <th
                          key={heading}
                          className={`border-b px-5 py-2 text-[11px] font-semibold uppercase ${
                            index > 1 ? 'text-right' : 'text-left'
                          }`}
                          style={{
                            borderColor: STROKE.base,
                            color: TEXT.secondary,
                            letterSpacing: '0.06em',
                          }}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recent.map((migration) => {
                      const status = deriveCampaignStatus(migration);
                      return (
                        <tr
                          key={migration.migrationId}
                          className="cursor-pointer hover:bg-black/[0.02]"
                          onClick={() => navigate(`/migrations/${encodeURIComponent(migration.migrationId)}/overview`)}
                        >
                          <td
                            className="border-b px-5 py-2.5"
                            style={{ borderColor: STROKE.subtle, color: TEXT.primary }}
                          >
                            {migration.migrationName}
                          </td>
                          <td
                            className="border-b px-5 py-2.5"
                            style={{ borderColor: STROKE.subtle, color: TEXT.secondary }}
                          >
                            {migrationTypeLabel(migration.migrationType)}
                          </td>
                          <td
                            className="border-b px-5 py-2.5 text-right"
                            style={{
                              borderColor: STROKE.subtle,
                              color: TEXT.primary,
                              ...NUMERIC,
                            }}
                          >
                            {migration.deviceCount.toLocaleString()}
                          </td>
                          <td
                            className="border-b px-5 py-2.5 text-right"
                            style={{ borderColor: STROKE.subtle }}
                          >
                            <StatePill state={status.state}>{status.label}</StatePill>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </QueryGate>
          </SectionPanel>

          <div>
            <SectionPanel
              title="Campaigns by wave state"
              description="Charted from wave credentials, not device progress."
            >
              <Donut
                segments={totals ? campaignsByWaveState(totals) : []}
                centerValue={(totals?.migrationCount ?? 0).toLocaleString()}
                centerLabel="campaigns"
              />
            </SectionPanel>

            {/* NO "Quick actions" PANEL (FD-482). Its two buttons are entries
                on the shell's persistent `New` menu (FD-481), present on this
                route and every other. One of them was also WRONG: "New
                campaign" navigated to `/migrations`, the LIST, leaving the
                operator to find the real create button on the page it dropped
                them on. */}
          </div>
        </div>
        </>
        )}
      </QueryGate>
    </>
  );
}
